import React, { useState, useEffect, useCallback } from 'react';
import outsourceService from '../../services/outsourceService';
import referenceLabService from '../../services/referenceLabService';
import masterService from '../../services/masterService';
import patientDocumentService from '../../services/patientDocumentService';
import { FILE_BASE_URL } from '../../services/api';
import CommonPagination from '../../components/common/CommonPagination';
import './OutsourceWorklistPage.css';

export const OutsourceWorklistPage = ({ showToast }) => {
  // State
  const [activeTab, setActiveTab] = useState('ALL');
  const [worklistItems, setWorklistItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLabId, setSelectedLabId] = useState('all');
  const [selectedDeptId, setSelectedDeptId] = useState('all');
  const [dateFilterMode, setDateFilterMode] = useState('ALL'); // 'ALL' | 'TODAY' | '7DAYS' | '30DAYS' | 'CUSTOM'
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Dropdown options
  const [referenceLabs, setReferenceLabs] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Selection for Batch Dispatch
  const [selectedTestIds, setSelectedTestIds] = useState([]);

  // Modals
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [batchForm, setBatchForm] = useState({
    referenceLabId: '',
    courierName: 'Blue Dart Express',
    trackingNo: '',
    remarks: ''
  });
  const [batchSaving, setBatchSaving] = useState(false);

  // Manifest Print Modal
  const [manifestModalOpen, setManifestModalOpen] = useState(false);
  const [currentManifest, setCurrentManifest] = useState(null);
  const [, setManifestLoading] = useState(false);

  // Result Entry Modal
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [activeOutsourceItem, setActiveOutsourceItem] = useState(null);
  const [resultForm, setResultForm] = useState({
    externalReferenceNo: '',
    externalResultSummary: '',
    attachmentUrl: '',
    remarks: ''
  });
  const [resultSaving, setResultSaving] = useState(false);
  const [uploadingReportFile, setUploadingReportFile] = useState(false);

  // Verification Modal
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyItem, setVerifyItem] = useState(null);
  const [verifyRemarks, setVerifyRemarks] = useState('');
  const [verifying, setVerifying] = useState(false);

  // Load dropdown lists
  useEffect(() => {
    const loadMasters = async () => {
      try {
        const [labsRes, deptsRes] = await Promise.all([
          referenceLabService.getReferenceLabs({ isActive: true }),
          masterService.getDepartments({ isActive: true })
        ]);
        if (labsRes.success) setReferenceLabs(labsRes.data || []);
        if (deptsRes.success) setDepartments(deptsRes.data || []);
      } catch (err) {
        console.error('Failed to load masters:', err);
      }
    };
    loadMasters();
  }, []);

  // Fetch KPI Summary
  const fetchSummary = useCallback(async () => {
    try {
      const res = await outsourceService.getSummary();
      if (res.success) setSummary(res.data);
    } catch (err) {
      console.error('Failed to fetch outsource summary:', err);
    }
  }, []);

  // Handle Quick Date Presets (Today, Last 7 Days, Last 30 Days, Custom)
  const handleDatePreset = (mode) => {
    setDateFilterMode(mode);
    setPageNumber(1);
    const now = new Date();
    if (mode === 'TODAY') {
      const todayStr = now.toISOString().split('T')[0];
      setFromDate(todayStr);
      setToDate(todayStr);
    } else if (mode === '7DAYS') {
      const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      setFromDate(sevenDaysAgo.toISOString().split('T')[0]);
      setToDate(now.toISOString().split('T')[0]);
    } else if (mode === '30DAYS') {
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setFromDate(thirtyDaysAgo.toISOString().split('T')[0]);
      setToDate(now.toISOString().split('T')[0]);
    } else if (mode === 'ALL') {
      setFromDate('');
      setToDate('');
    }
  };

  // Fetch Worklist
  const fetchWorklist = useCallback(async () => {
    setLoading(true);
    try {
      const res = await outsourceService.getWorklist({
        status: activeTab === 'ALL' ? undefined : activeTab,
        referenceLabId: selectedLabId === 'all' ? undefined : parseInt(selectedLabId, 10),
        departmentId: selectedDeptId === 'all' ? undefined : parseInt(selectedDeptId, 10),
        search: searchQuery.trim() || undefined,
        fromDate: fromDate ? `${fromDate}T00:00:00` : undefined,
        toDate: toDate ? `${toDate}T23:59:59` : undefined,
        pageNumber,
        pageSize
      });
      if (res.success && res.data) {
        setWorklistItems(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
      }
    } catch (err) {
      showToast?.('Failed to load outsource worklist.', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedLabId, selectedDeptId, searchQuery, fromDate, toDate, pageNumber, pageSize, showToast]);

  useEffect(() => {
    fetchWorklist();
    fetchSummary();
  }, [fetchWorklist, fetchSummary]);

  // Selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const selectable = worklistItems
        .filter(item => item.status === 'READY_FOR_DISPATCH')
        .map(item => item.testId);
      setSelectedTestIds(selectable);
    } else {
      setSelectedTestIds([]);
    }
  };

  const handleToggleSelect = (testId) => {
    setSelectedTestIds(prev => 
      prev.includes(testId) ? prev.filter(id => id !== testId) : [...prev, testId]
    );
  };

  // Open Batch Dispatch Modal
  const handleOpenBatchModal = () => {
    if (selectedTestIds.length === 0) {
      showToast?.('Please select at least one specimen for dispatch.', 'warning');
      return;
    }
    // Determine default reference lab from selected items
    const selectedItems = worklistItems.filter(item => selectedTestIds.includes(item.testId));
    const firstLabId = selectedItems[0]?.referenceLabId || '';

    setBatchForm({
      referenceLabId: firstLabId,
      courierName: 'Blue Dart Logistics',
      trackingNo: `BD-${Math.floor(10000000 + Math.random() * 90000000)}`,
      remarks: `Dispatch of ${selectedTestIds.length} sample(s)`
    });
    setBatchModalOpen(true);
  };

  // Submit Batch Dispatch
  const handleCreateBatch = async (e) => {
    if (e) e.preventDefault();
    if (!batchForm.referenceLabId) {
      showToast?.('Select target reference laboratory.', 'error');
      return;
    }
    if (!batchForm.courierName.trim()) {
      showToast?.('Courier name is mandatory.', 'error');
      return;
    }

    setBatchSaving(true);
    try {
      const res = await outsourceService.createDispatchBatch({
        referenceLabId: parseInt(batchForm.referenceLabId, 10),
        courierName: batchForm.courierName.trim(),
        trackingNo: batchForm.trackingNo.trim() || null,
        remarks: batchForm.remarks.trim() || null,
        testIds: selectedTestIds
      });

      if (res.success && res.data) {
        showToast?.(`Dispatch batch created successfully with ${selectedTestIds.length} samples.`, 'success');
        setBatchModalOpen(false);
        setSelectedTestIds([]);
        setCurrentManifest(res.data);
        setManifestModalOpen(true);
        fetchWorklist();
        fetchSummary();
      }
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Failed to create dispatch batch.', 'error');
    } finally {
      setBatchSaving(false);
    }
  };

  // Open Manifest Slip for any batch
  const handleViewManifest = async (batchId) => {
    setManifestLoading(true);
    try {
      const res = await outsourceService.getBatchManifest(batchId);
      if (res.success) {
        setCurrentManifest(res.data);
        setManifestModalOpen(true);
      }
    } catch (err) {
      showToast?.('Failed to load batch manifest.', 'error');
    } finally {
      setManifestLoading(false);
    }
  };

  // Open Result Entry Modal
  const handleOpenResultModal = (item) => {
    setActiveOutsourceItem(item);
    setResultForm({
      externalReferenceNo: item.externalReferenceNo || '',
      externalResultSummary: item.externalResultSummary || '',
      attachmentUrl: item.externalReportAttachmentUrl || '',
      remarks: item.remarks || ''
    });
    setResultModalOpen(true);
  };

  // Upload External Report Document (PDF / Image / Scanned Copy)
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeOutsourceItem) return;
    setUploadingReportFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('patientId', activeOutsourceItem.patientId || '');
      if (activeOutsourceItem.ledgerTransactionNo) {
        formData.append('ledgerTransactionNo', activeOutsourceItem.ledgerTransactionNo);
      }
      formData.append('documentType', 'OUTSOURCE_REPORT');
      formData.append(
        'documentName',
        `${activeOutsourceItem.investigationName || 'Outsource'}_${activeOutsourceItem.referenceLabName || 'Partner'}_Report`
      );

      const res = await patientDocumentService.uploadDocument(formData);
      if (res && res.data) {
        const fileUrl = res.data.viewUrl?.startsWith('http')
          ? res.data.viewUrl
          : `${FILE_BASE_URL}${res.data.viewUrl}`;
        setResultForm((prev) => ({ ...prev, attachmentUrl: fileUrl }));
        showToast?.('Reference lab report uploaded successfully!', 'success');
      }
    } catch (err) {
      console.error('File upload error:', err);
      showToast?.(err.response?.data?.message || 'File upload failed. You may also paste a URL link.', 'error');
    } finally {
      setUploadingReportFile(false);
    }
  };

  // Submit Result Entry
  const handleSaveResult = async (e) => {
    if (e) e.preventDefault();
    if (!resultForm.externalResultSummary.trim()) {
      showToast?.('Please enter the external result summary / clinical findings.', 'error');
      return;
    }

    setResultSaving(true);
    try {
      await outsourceService.recordResult({
        testId: activeOutsourceItem.testId,
        externalReferenceNo: resultForm.externalReferenceNo.trim() || null,
        externalResultSummary: resultForm.externalResultSummary.trim(),
        attachmentUrl: resultForm.attachmentUrl.trim() || null,
        remarks: resultForm.remarks.trim() || null
      });

      showToast?.('External result recorded successfully.', 'success');
      setResultModalOpen(false);
      fetchWorklist();
      fetchSummary();
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Failed to save external result.', 'error');
    } finally {
      setResultSaving(false);
    }
  };

  // Open Verification Modal
  const handleOpenVerifyModal = (item) => {
    setVerifyItem(item);
    setVerifyRemarks('');
    setVerifyModalOpen(true);
  };

  // Execute Verification
  const handleVerify = async () => {
    if (!verifyItem) return;
    setVerifying(true);
    try {
      await outsourceService.verifyResult({
        testId: verifyItem.testId,
        remarks: verifyRemarks.trim() || 'Clinically verified and authorized'
      });

      showToast?.('Outsource test verified and released to patient report successfully.', 'success');
      setVerifyModalOpen(false);
      fetchWorklist();
      fetchSummary();
    } catch (err) {
      showToast?.(err.response?.data?.message || 'Verification failed.', 'error');
    } finally {
      setVerifying(false);
    }
  };

  // Status badge styling helper
  const getStatusBadge = (status) => {
    switch (status) {
      case 'READY_FOR_DISPATCH':
        return <span className="outsource-badge badge-warning">Ready for Dispatch</span>;
      case 'DISPATCHED':
        return <span className="outsource-badge badge-info">Dispatched</span>;
      case 'IN_TRANSIT':
        return <span className="outsource-badge badge-primary">In Transit</span>;
      case 'RECEIVED_AT_LAB':
        return <span className="outsource-badge badge-indigo">At Partner Lab</span>;
      case 'RESULT_RECEIVED':
        return <span className="outsource-badge badge-purple">Result Received</span>;
      case 'VERIFIED':
        return <span className="outsource-badge badge-success">Verified & Released</span>;
      case 'REJECTED':
        return <span className="outsource-badge badge-danger">Rejected</span>;
      default:
        return <span className="outsource-badge badge-secondary">{status}</span>;
    }
  };

  return (
    <div className="outsource-worklist-container">
      {/* Header */}
      <div className="outsource-header">
        <div>
          <h1 className="outsource-title">Outsource & Reference Lab Hub</h1>
          <p className="outsource-subtitle">
            Universal sample dispatch, courier tracking, manifest generation & external partner result verification
          </p>
        </div>
        <div className="outsource-header-actions">
          {selectedTestIds.length > 0 && (
            <button
              type="button"
              className="btn btn-warning"
              onClick={handleOpenBatchModal}
            >
              📦 Create Dispatch Batch ({selectedTestIds.length})
            </button>
          )}
        </div>
      </div>

      {/* KPI Summary Cards */}
      {summary && (
        <div className="outsource-kpi-grid">
          <div className="kpi-card" onClick={() => setActiveTab('ALL')}>
            <div className="kpi-label">Total Outsourced</div>
            <div className="kpi-value text-primary">{summary.totalOutsourced}</div>
          </div>
          <div className="kpi-card" onClick={() => setActiveTab('READY_FOR_DISPATCH')}>
            <div className="kpi-label">Ready for Dispatch</div>
            <div className="kpi-value text-warning">{summary.readyForDispatchCount}</div>
          </div>
          <div className="kpi-card" onClick={() => setActiveTab('DISPATCHED')}>
            <div className="kpi-label">Dispatched / Transit</div>
            <div className="kpi-value text-info">{summary.dispatchedCount}</div>
          </div>
          <div className="kpi-card" onClick={() => setActiveTab('RECEIVED_AT_LAB')}>
            <div className="kpi-label">At Partner Lab</div>
            <div className="kpi-value text-indigo">{summary.atLabCount}</div>
          </div>
          <div className="kpi-card" onClick={() => setActiveTab('RESULT_RECEIVED')}>
            <div className="kpi-label">Result Pending Verification</div>
            <div className="kpi-value text-purple">{summary.resultReceivedCount}</div>
          </div>
          <div className="kpi-card" onClick={() => setActiveTab('VERIFIED')}>
            <div className="kpi-label">Verified & Released</div>
            <div className="kpi-value text-success">{summary.verifiedCount}</div>
          </div>
          <div className="kpi-card" style={{ borderLeft: '4px solid #ef4444' }}>
            <div className="kpi-label">Overdue TAT</div>
            <div className="kpi-value text-danger">{summary.overdueCount || 0}</div>
          </div>
          <div className="kpi-card" style={{ borderLeft: '4px solid #10b981' }}>
            <div className="kpi-label">Est. B2B Outsource Cost</div>
            <div className="kpi-value text-success">
              ₹{worklistItems.reduce((acc, curr) => acc + (parseFloat(curr.externalRate) || 0), 0).toLocaleString('en-IN')}
            </div>
          </div>
        </div>
      )}

      {/* Date Range Presets & Filter Bar */}
      <div style={{ background: '#ffffff', padding: '12px 18px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>📅 Outsource Date Range:</span>
          {[
            { key: 'ALL', label: 'All Time' },
            { key: 'TODAY', label: '⚡ Today' },
            { key: '7DAYS', label: '7 Days' },
            { key: '30DAYS', label: '30 Days / 1 Month' },
            { key: 'CUSTOM', label: 'Custom Range' },
          ].map(p => (
            <button
              key={p.key}
              type="button"
              className={`btn btn-sm ${dateFilterMode === p.key ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => handleDatePreset(p.key)}
              style={{ borderRadius: '20px', padding: '3px 12px', fontSize: '0.8rem', fontWeight: 600 }}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Date Inputs if Custom or Active */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>From:</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setDateFilterMode('CUSTOM');
                setPageNumber(1);
              }}
              style={{ width: '135px' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>To:</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setDateFilterMode('CUSTOM');
                setPageNumber(1);
              }}
              style={{ width: '135px' }}
            />
          </div>
          {(fromDate || toDate) && (
            <button
              type="button"
              className="btn btn-sm btn-link text-danger p-0"
              onClick={() => handleDatePreset('ALL')}
              title="Reset dates"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Status Tabs */}
      <div className="outsource-tabs">
        {[
          { key: 'ALL', label: 'All Samples' },
          { key: 'READY_FOR_DISPATCH', label: 'Ready for Dispatch' },
          { key: 'DISPATCHED', label: 'Dispatched' },
          { key: 'RECEIVED_AT_LAB', label: 'At Partner Lab' },
          { key: 'RESULT_RECEIVED', label: 'Result Received' },
          { key: 'VERIFIED', label: 'Verified & Released' }
        ].map(tab => (
          <button
            key={tab.key}
            type="button"
            className={`outsource-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.key);
              setPageNumber(1);
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters Bar */}
      <div className="outsource-filters-bar">
        <div className="filter-item search-box">
          <input
            type="text"
            className="form-control"
            placeholder="Search by Barcode, Patient, Order #, Test..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPageNumber(1);
            }}
          />
        </div>

        <div className="filter-item">
          <select
            className="form-control"
            value={selectedLabId}
            onChange={(e) => {
              setSelectedLabId(e.target.value);
              setPageNumber(1);
            }}
          >
            <option value="all">All Reference Labs</option>
            {referenceLabs.map(lab => (
              <option key={lab.referenceLabId} value={lab.referenceLabId}>{lab.labName}</option>
            ))}
          </select>
        </div>

        <div className="filter-item">
          <select
            className="form-control"
            value={selectedDeptId}
            onChange={(e) => {
              setSelectedDeptId(e.target.value);
              setPageNumber(1);
            }}
          >
            <option value="all">All Departments</option>
            {departments.map(d => (
              <option key={d.subCategoryId} value={d.subCategoryId}>{d.displayName || d.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Worklist Table */}
      <div className="table-responsive">
        <table className="table outsource-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={
                    worklistItems.filter(i => i.status === 'READY_FOR_DISPATCH').length > 0 &&
                    selectedTestIds.length === worklistItems.filter(i => i.status === 'READY_FOR_DISPATCH').length
                  }
                />
              </th>
              <th style={{ width: '130px' }}>Barcode / Order</th>
              <th>Patient Details</th>
              <th>Investigation & Department</th>
              <th>Target Reference Lab</th>
              <th style={{ width: '130px' }}>Logistics / Courier</th>
              <th style={{ width: '130px', textAlign: 'center' }}>Status</th>
              <th style={{ width: '170px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center py-5 text-muted">
                  Loading outsource specimens...
                </td>
              </tr>
            ) : worklistItems.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-5 text-muted">
                  No outsourced tests found for the selected criteria.
                </td>
              </tr>
            ) : (
              worklistItems.map((item) => (
                <tr key={item.outsourceId} className={selectedTestIds.includes(item.testId) ? 'row-selected' : ''}>
                  <td>
                    {item.status === 'READY_FOR_DISPATCH' ? (
                      <input
                        type="checkbox"
                        checked={selectedTestIds.includes(item.testId)}
                        onChange={() => handleToggleSelect(item.testId)}
                      />
                    ) : (
                      <span className="text-muted">•</span>
                    )}
                  </td>
                  <td>
                    <div className="barcode-display">
                      <strong>{item.barcodeNo || 'NO-BARCODE'}</strong>
                    </div>
                    <div className="text-muted small">{item.ledgerTransactionNo}</div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{item.patientName}</div>
                    <div className="text-muted small">
                      {item.patientId} • {item.patientAge} • {item.patientGender}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{item.investigationName}</div>
                    <div className="text-muted small">
                      <span className="badge badge-light mr-1" style={{ marginRight: '4px' }}>{item.investigationCode}</span>
                      {item.departmentName}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{item.referenceLabName}</div>
                    <div className="text-muted small">{item.referenceLabCode}</div>
                    {item.externalRate != null && Number(item.externalRate) > 0 && (
                      <div style={{ fontSize: '0.78rem', color: '#047857', fontWeight: 600, marginTop: '2px' }}>
                        B2B Rate: ₹{Number(item.externalRate).toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td>
                    {item.dispatchBatchId ? (
                      <div>
                        <button
                          type="button"
                          className="btn-link p-0 text-left"
                          onClick={() => handleViewManifest(item.dispatchBatchId)}
                          style={{ background: 'none', border: 'none', color: '#0284c7', cursor: 'pointer', fontWeight: 600 }}
                        >
                          {item.batchNo}
                        </button>
                        <div className="text-muted small">
                          {item.courierName}: {item.trackingNo}
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted small">Not Dispatched</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {getStatusBadge(item.status)}
                    {item.expectedTat && (
                      <div
                        className="small mt-1"
                        style={{
                          fontSize: '0.76rem',
                          color: new Date(item.expectedTat) < new Date() && item.status !== 'VERIFIED' && item.status !== 'RESULT_RECEIVED' ? '#dc2626' : '#64748b',
                          fontWeight: new Date(item.expectedTat) < new Date() && item.status !== 'VERIFIED' && item.status !== 'RESULT_RECEIVED' ? 700 : 500
                        }}
                      >
                        {new Date(item.expectedTat) < new Date() && item.status !== 'VERIFIED' && item.status !== 'RESULT_RECEIVED' ? (
                          <span className="badge badge-danger p-1" style={{ backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}>
                            ⚠️ Overdue ({new Date(item.expectedTat).toLocaleDateString([], { month: 'short', day: 'numeric' })})
                          </span>
                        ) : (
                          <span>TAT: {new Date(item.expectedTat).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {item.status === 'READY_FOR_DISPATCH' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => {
                          setSelectedTestIds([item.testId]);
                          handleOpenBatchModal();
                        }}
                      >
                        Dispatch
                      </button>
                    )}

                    {(item.status === 'DISPATCHED' || item.status === 'IN_TRANSIT' || item.status === 'RECEIVED_AT_LAB') && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleOpenResultModal(item)}
                      >
                        Record Result
                      </button>
                    )}

                    {item.status === 'RESULT_RECEIVED' && (
                      <div className="d-flex justify-content-end gap-1">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary mr-1"
                          onClick={() => handleOpenResultModal(item)}
                          style={{ marginRight: '4px' }}
                        >
                          Edit Result
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-success"
                          onClick={() => handleOpenVerifyModal(item)}
                        >
                          Verify & Release
                        </button>
                      </div>
                    )}

                    {item.status === 'VERIFIED' && (
                      <span className="text-success small font-weight-bold">
                        ✓ Verified by {item.verifiedBy || 'Reviewer'}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Centralized Pagination */}
      <CommonPagination
        page={pageNumber}
        pageSize={pageSize}
        totalRecords={totalCount}
        onPageChange={setPageNumber}
        onPageSizeChange={setPageSize}
        pageSizeOptions={[15, 25, 50, 100, 200]}
      />

      {/* Create Dispatch Batch Modal */}
      {batchModalOpen && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <h3>Create Outsource Dispatch Batch</h3>
              <button type="button" className="close-btn" onClick={() => setBatchModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleCreateBatch}>
              <div className="modal-body">
                <p className="text-muted">
                  You are preparing a dispatch batch for <strong>{selectedTestIds.length}</strong> selected specimen(s).
                </p>

                <div className="form-group mb-3">
                  <label className="form-label required">Target Reference Laboratory</label>
                  <select
                    className="form-control"
                    value={batchForm.referenceLabId}
                    onChange={(e) => setBatchForm(prev => ({ ...prev, referenceLabId: e.target.value }))}
                    required
                  >
                    <option value="">-- Select Reference Lab --</option>
                    {referenceLabs.map(lab => (
                      <option key={lab.referenceLabId} value={lab.referenceLabId}>
                        {lab.labCode} - {lab.labName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-grid-2 mb-3">
                  <div className="form-group">
                    <label className="form-label required">Courier / Transport Agency</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Blue Dart, DTDC, Dedicated Runner"
                      value={batchForm.courierName}
                      onChange={(e) => setBatchForm(prev => ({ ...prev, courierName: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Airway Bill / Tracking Number</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. AWB-9827162"
                      value={batchForm.trackingNo}
                      onChange={(e) => setBatchForm(prev => ({ ...prev, trackingNo: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Batch Logistics Remarks</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    placeholder="Temperature / Cold chain notes, package seal number, etc."
                    value={batchForm.remarks}
                    onChange={(e) => setBatchForm(prev => ({ ...prev, remarks: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setBatchModalOpen(false)}
                  disabled={batchSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={batchSaving}
                >
                  {batchSaving ? 'Generating Batch...' : 'Generate Dispatch Manifest'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manifest Slip Modal */}
      {manifestModalOpen && currentManifest && (
        <div className="modal-backdrop">
          <div className="modal-card modal-lg">
            <div className="modal-header">
              <div>
                <h3>Outsource Dispatch Manifest: {currentManifest.batchNo}</h3>
                <span className="badge badge-info">{currentManifest.status}</span>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn-outline-primary mr-2"
                  onClick={() => window.print()}
                  style={{ marginRight: '8px' }}
                >
                  🖨️ Print Manifest Slip
                </button>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setManifestModalOpen(false)}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="modal-body printable-manifest">
              {/* Manifest Meta */}
              <div className="manifest-grid-card">
                <div>
                  <strong>Destination Lab:</strong> {currentManifest.referenceLabName} ({currentManifest.referenceLabCode})
                  <div className="text-muted small">{currentManifest.referenceLabAddress}</div>
                </div>
                <div>
                  <strong>Courier:</strong> {currentManifest.courierName}
                  <div><strong>AWB / Tracking:</strong> {currentManifest.trackingNo || 'N/A'}</div>
                </div>
                <div>
                  <strong>Total Samples:</strong> {currentManifest.totalSamples}
                  <div><strong>Dispatched By:</strong> {currentManifest.dispatchedBy || 'Staff'}</div>
                </div>
              </div>

              {/* Manifest Samples Table */}
              <table className="table manifest-table mt-3">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Barcode</th>
                    <th>Order #</th>
                    <th>Patient UHID & Name</th>
                    <th>Internal Test</th>
                    <th>External Test Code & Name</th>
                  </tr>
                </thead>
                <tbody>
                  {(currentManifest.items || []).map((item, idx) => (
                    <tr key={item.outsourceId || idx}>
                      <td>{idx + 1}</td>
                      <td><strong>{item.barcodeNo}</strong></td>
                      <td>{item.ledgerTransactionNo}</td>
                      <td>
                        <strong>{item.patientName}</strong> ({item.patientId})
                      </td>
                      <td>{item.investigationName}</td>
                      <td>
                        <span className="badge badge-light mr-1">{item.externalTestCode}</span>
                        {item.externalTestName || item.investigationName}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="manifest-signatures mt-4">
                <div className="sig-box">
                  <div className="sig-line">Prepared & Dispatched By</div>
                  <div className="text-muted small">Signature & Date</div>
                </div>
                <div className="sig-box">
                  <div className="sig-line">Courier Pickup Agent</div>
                  <div className="text-muted small">Signature & Employee ID</div>
                </div>
                <div className="sig-box">
                  <div className="sig-line">Reference Lab Received By</div>
                  <div className="text-muted small">Seal & Timestamp</div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setManifestModalOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record External Result Modal */}
      {resultModalOpen && activeOutsourceItem && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <h3>Record External Partner Result</h3>
              <button type="button" className="close-btn" onClick={() => setResultModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleSaveResult}>
              <div className="modal-body">
                <div className="info-banner mb-3">
                  <div><strong>Patient:</strong> {activeOutsourceItem.patientName} ({activeOutsourceItem.patientId})</div>
                  <div><strong>Investigation:</strong> {activeOutsourceItem.investigationName} ({activeOutsourceItem.referenceLabName})</div>
                  <div><strong>Barcode:</strong> {activeOutsourceItem.barcodeNo}</div>
                </div>

                <div className="form-group mb-3">
                  <label className="form-label">External Partner Reference / Report #</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. QDL-REP-2026-99120"
                    value={resultForm.externalReferenceNo}
                    onChange={(e) => setResultForm(prev => ({ ...prev, externalReferenceNo: e.target.value }))}
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label required">External Clinical Result / Findings Summary</label>
                  <textarea
                    className="form-control"
                    rows={4}
                    placeholder="Enter numeric value, interpretation, or structured clinical findings received from reference lab..."
                    value={resultForm.externalResultSummary}
                    onChange={(e) => setResultForm(prev => ({ ...prev, externalResultSummary: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="form-label font-weight-bold">Upload External Report (PDF / Scanned Hardcopy / Image)</label>
                  <input
                    type="file"
                    className="form-control"
                    accept=".pdf,image/jpeg,image/png,image/jpg"
                    onChange={handleFileUpload}
                    disabled={uploadingReportFile || resultSaving}
                  />
                  <small className="text-muted d-block mt-1">
                    💡 Scan physical letterhead hardcopy to PDF/image, or directly choose softcopy PDF received from partner.
                  </small>
                  {uploadingReportFile && (
                    <div className="mt-1 text-primary small">⏳ Uploading report to patient repository...</div>
                  )}
                  {resultForm.attachmentUrl && (
                    <div className="mt-2 p-2 bg-light border rounded d-flex align-items-center justify-content-between">
                      <span className="small text-success font-weight-bold">✅ Report Linked</span>
                      <a
                        href={resultForm.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary py-0"
                      >
                        👁️ Preview Attachment ↗
                      </a>
                    </div>
                  )}
                </div>

                <div className="form-group mb-3">
                  <label className="form-label text-muted small">Or External Report Web Link (Optional)</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="e.g. https://portal.questreference.com/reports/99120.pdf"
                    value={resultForm.attachmentUrl}
                    onChange={(e) => setResultForm(prev => ({ ...prev, attachmentUrl: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Review Remarks</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Optional technician or liaison comments"
                    value={resultForm.remarks}
                    onChange={(e) => setResultForm(prev => ({ ...prev, remarks: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setResultModalOpen(false)}
                  disabled={resultSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={resultSaving}
                >
                  {resultSaving ? 'Saving...' : 'Save Result'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {verifyModalOpen && verifyItem && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <h3>Clinical Authorization & Release</h3>
              <button type="button" className="close-btn" onClick={() => setVerifyModalOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p>
                Are you sure you want to verify and clinically release the outsourced result for:
              </p>
              <div className="info-banner mb-3">
                <div><strong>Patient:</strong> {verifyItem.patientName}</div>
                <div><strong>Investigation:</strong> {verifyItem.investigationName}</div>
                <div><strong>External Partner:</strong> {verifyItem.referenceLabName}</div>
                <div><strong>Result Summary:</strong> {verifyItem.externalResultSummary}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Verification Sign-off Remarks</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Clinically verified and authorized for final report"
                  value={verifyRemarks}
                  onChange={(e) => setVerifyRemarks(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setVerifyModalOpen(false)}
                disabled={verifying}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleVerify}
                disabled={verifying}
              >
                {verifying ? 'Verifying...' : 'Authorize & Release Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutsourceWorklistPage;
